import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auto-logout on expired/invalid token
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 || error.response?.status === 403) {
      const isAuthRoute = error.config?.url?.includes('/auth/');
      if (!isAuthRoute) {
        const errorMsg = error.response?.data?.error || '';
        if (errorMsg.includes('expired') || errorMsg.includes('Invalid or expired token') || error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login?session=expired';
        }
      }
    }
    return Promise.reject(error);
  }
);

export const healthCheck = async () => {
  const response = await api.get('/health');
  return response.data;
};

export const authAPI = {
  login: async (email: string, password: string) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  register: async (userData: any) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  getProfile: async () => {
    const response = await api.get('/auth/profile');
    return response.data;
  },
  updateProfile: async (profile: Partial<{ city: string; state: string; zipCode: string; phone: string; latitude: number; longitude: number; skillLevel: string; ageGroup: string; position: string; availability: string }>) => {
    const response = await api.patch('/auth/profile', profile);
    return response.data;
  },
  forgotPassword: async (email: string) => {
    const response = await api.post('/auth/forgot-password', { email });
    return response.data;
  },
  resetPassword: async (token: string, password: string) => {
    const response = await api.post('/auth/reset-password', { token, password });
    return response.data;
  },
};

export const teamsAPI = {
  getAll: async () => {
    const response = await api.get('/teams');
    return response.data.data || response.data;
  },
  getById: async (id: string) => {
    const response = await api.get(`/teams/${id}`);
    return response.data;
  },
  create: async (teamData: any) => {
    const response = await api.post('/teams', teamData);
    return response.data;
  },
  update: async (id: string, teamData: any) => {
    const response = await api.put(`/teams/${id}`, teamData);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/teams/${id}`);
    return response.data;
  },
};

export const postsAPI = {
  getAll: async (filters?: any) => {
    const response = await api.get('/posts', { params: filters });
    return response.data.data || response.data;
  },
  getById: async (id: string) => {
    const response = await api.get(`/posts/${id}`);
    return response.data;
  },
  create: async (postData: any) => {
    const response = await api.post('/posts', postData);
    return response.data;
  },
  update: async (id: string, postData: any) => {
    const response = await api.put(`/posts/${id}`, postData);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/posts/${id}`);
    return response.data;
  },
  toggleFulfilled: async (id: string) => {
    const response = await api.patch(`/posts/${id}/fulfill`);
    return response.data.post;
  },
};

export const matchesAPI = {
  getMyMatches: async (params?: { sport?: string; minScore?: number; limit?: number; radius?: number }) => {
    const response = await api.get('/matches/me', { params });
    return response.data;
  },
  calculateMatch: async (postId: string) => {
    const response = await api.post('/matches/calculate', { postId });
    return response.data;
  },
  getTopPosts: async (params?: { limit?: number; sport?: string }) => {
    const response = await api.get('/matches/top', { params });
    return response.data;
  },
};

export const favoritesAPI = {
  getAll: async () => {
    const response = await api.get('/favorites');
    return response.data;
  },
  add: async (postId: string) => {
    const response = await api.post(`/favorites/${postId}`);
    return response.data;
  },
  remove: async (postId: string) => {
    const response = await api.delete(`/favorites/${postId}`);
    return response.data;
  },
};

export interface Message {
  id: string;
  senderId: string;
  receiverId: string;
  postId?: string;
  content: string;
  isRead: boolean;
  createdAt: string;
  sender?: {
    id: string;
    username: string;
    email: string;
  };
  receiver?: {
    id: string;
    username: string;
    email: string;
  };
  post?: {
    id: string;
    title: string;
    sport: string;
  };
}

export interface Conversation {
  partnerId: string;
  partnerName: string;
  partnerEmail: string;
  lastMessage: string;
  lastMessageAt: string;
  unreadCount: number;
}

export const messagesAPI = {
  getConversations: async () => {
    const response = await api.get<{ conversations: Conversation[] }>('/messages/conversations');
    return response.data.conversations;
  },
  getThread: async (partnerId: string) => {
    const response = await api.get<{ messages: Message[] }>(`/messages/${partnerId}`);
    return response.data.messages;
  },
  send: async (receiverId: string, content: string, postId?: string) => {
    const response = await api.post<{ message: Message }>('/messages', { receiverId, content, postId });
    return response.data.message;
  },
  markAsRead: async (messageId: string) => {
    const response = await api.patch<Message>(`/messages/${messageId}/read`);
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get<{ count: number }>('/messages/unread/count');
    return response.data;
  },
};

export interface Notification {
  id: string;
  userId: string;
  type: 'new_message' | 'match_found' | 'post_response' | 'favorite_added' | 'team_invite' | 'system';
  title: string;
  message: string;
  isRead: boolean;
  relatedId?: string;
  relatedType?: 'post' | 'message' | 'team' | 'user';
  actionUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export const notificationsAPI = {
  getAll: async (params?: { type?: string; isRead?: boolean; limit?: number; offset?: number }) => {
    const response = await api.get<{ notifications: Notification[]; unreadCount: number }>('/notifications', { params });
    return response.data;
  },
  getUnreadCount: async () => {
    const response = await api.get<{ count: number }>('/notifications/unread/count');
    return response.data;
  },
  markAsRead: async (notificationId: string) => {
    const response = await api.patch<Notification>(`/notifications/${notificationId}/read`);
    return response.data;
  },
  markAllAsRead: async () => {
    const response = await api.patch('/notifications/mark-all-read');
    return response.data;
  },
  delete: async (notificationId: string) => {
    const response = await api.delete(`/notifications/${notificationId}`);
    return response.data;
  },
};

export interface SavedFilter {
  id: string;
  userId: string;
  name: string;
  filters: Record<string, any>;
  isDefault: boolean;
  emailAlerts: boolean;
  lastNotifiedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const savedFiltersAPI = {
  getAll: async () => {
    const response = await api.get<SavedFilter[]>('/saved-filters');
    return response.data;
  },
  create: async (name: string, filters: Record<string, any>, isDefault?: boolean) => {
    const response = await api.post<SavedFilter>('/saved-filters', { name, filters, isDefault });
    return response.data;
  },
  update: async (id: string, name: string, filters: Record<string, any>, isDefault?: boolean) => {
    const response = await api.put<SavedFilter>(`/saved-filters/${id}`, { name, filters, isDefault });
    return response.data;
  },
  setEmailAlerts: async (id: string, emailAlerts: boolean) => {
    const response = await api.put<SavedFilter>(`/saved-filters/${id}`, { emailAlerts });
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/saved-filters/${id}`);
    return response.data;
  },
};

// Team Management Types and APIs
export interface TeamMember {
  id: string;
  teamId: string;
  userId: string;
  role: 'player' | 'coach' | 'assistant_coach' | 'captain' | 'manager';
  jerseyNumber?: number;
  status: 'active' | 'inactive' | 'injured' | 'pending';
  joinedAt: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    city: string;
    state: string;
  };
  team?: {
    id: string;
    name: string;
    sport: string;
    city: string;
    state: string;
    ageGroup?: string;
    skillLevel?: string;
  };
}

export interface Event {
  id: string;
  teamId: string;
  type: 'practice' | 'game' | 'tournament' | 'meeting' | 'tryout' | 'other';
  title: string;
  description?: string;
  location?: string;
  startTime: string;
  endTime?: string;
  opponent?: string;
  result: 'win' | 'loss' | 'tie' | 'pending';
  score?: string;
  isAllDay: boolean;
  isCancelled: boolean;
  notes?: string;
  attendance?: EventAttendance[];
  createdAt: string;
  updatedAt: string;
}

export interface EventAttendance {
  id: string;
  eventId: string;
  userId: string;
  status: 'attending' | 'not_attending' | 'maybe' | 'no_response';
  respondedAt?: string;
  notes?: string;
  user?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export const teamMembersAPI = {
  getMembers: async (teamId: string, status?: string) => {
    const response = await api.get<{ members: TeamMember[] }>(`/teams/${teamId}/members`, { params: { status } });
    return response.data.members;
  },
  getRoster: async (teamId: string) => {
    const response = await api.get<{ roster: any }>(`/teams/${teamId}/roster`);
    return response.data.roster;
  },
  addMember: async (teamId: string, userId: string, role: string, jerseyNumber?: number) => {
    const response = await api.post<{ member: TeamMember }>(`/teams/${teamId}/members`, { userId, role, jerseyNumber });
    return response.data.member;
  },
  updateMember: async (teamId: string, memberId: string, updates: Partial<{ role: string; status: string; jerseyNumber: number }>) => {
    const response = await api.patch<{ member: TeamMember }>(`/teams/${teamId}/members/${memberId}`, updates);
    return response.data.member;
  },
  removeMember: async (teamId: string, memberId: string) => {
    const response = await api.delete(`/teams/${teamId}/members/${memberId}`);
    return response.data;
  },
  getMyMemberships: async () => {
    const response = await api.get<{ memberships: TeamMember[] }>('/teams/my-memberships');
    return response.data.memberships;
  },
  importMembers: async (
    teamId: string,
    members: Array<{ email: string; role?: string; jerseyNumber?: string }>
  ) => {
    const response = await api.post<{
      added: Array<{ email: string; role: string; jerseyNumber: string | null }>;
      notFound: string[];
      alreadyMember: string[];
    }>(`/teams/${teamId}/members/import`, { members });
    return response.data;
  },
};

export const teamEventsAPI = {
  getEvents: async (teamId: string, params?: { type?: string; startDate?: string; endDate?: string; includeAttendance?: boolean }) => {
    const response = await api.get<{ events: Event[] }>(`/teams/${teamId}/events`, { params });
    return response.data.events;
  },
  getEvent: async (teamId: string, eventId: string) => {
    const response = await api.get<{ event: Event }>(`/teams/${teamId}/events/${eventId}`);
    return response.data.event;
  },
  createEvent: async (teamId: string, eventData: Partial<Event>) => {
    const response = await api.post<{ event: Event }>(`/teams/${teamId}/events`, eventData);
    return response.data.event;
  },
  updateEvent: async (teamId: string, eventId: string, eventData: Partial<Event>) => {
    const response = await api.patch<{ event: Event }>(`/teams/${teamId}/events/${eventId}`, eventData);
    return response.data.event;
  },
  deleteEvent: async (teamId: string, eventId: string) => {
    const response = await api.delete(`/teams/${teamId}/events/${eventId}`);
    return response.data;
  },
  updateAttendance: async (teamId: string, eventId: string, status: string, notes?: string) => {
    const response = await api.post<{ attendance: EventAttendance }>(`/teams/${teamId}/events/${eventId}/attendance`, { status, notes });
    return response.data.attendance;
  },
  getAttendance: async (teamId: string, eventId: string) => {
    const response = await api.get<{ attendance: any }>(`/teams/${teamId}/events/${eventId}/attendance`);
    return response.data.attendance;
  },
  getAttendanceStats: async (teamId: string) => {
    const response = await api.get<{
      totalEvents: number;
      stats: Array<{
        memberId: string;
        userId: string;
        name: string;
        role: string;
        jerseyNumber: string | null;
        attendingCount: number;
        totalEvents: number;
        rate: number | null;
      }>;
    }>(`/teams/${teamId}/attendance-stats`);
    return response.data;
  },
};

export interface Announcement {
  id: string;
  teamId: string;
  authorId: string;
  title: string;
  content: string;
  isPinned: boolean;
  createdAt: string;
  updatedAt: string;
  author?: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
}

export const announcementsAPI = {
  getAnnouncements: async (teamId: string) => {
    const response = await api.get<{ announcements: Announcement[] }>(`/teams/${teamId}/announcements`);
    return response.data.announcements;
  },
  createAnnouncement: async (teamId: string, title: string, content: string, isPinned?: boolean) => {
    const response = await api.post<{ announcement: Announcement }>(`/teams/${teamId}/announcements`, { title, content, isPinned });
    return response.data.announcement;
  },
  updateAnnouncement: async (teamId: string, announcementId: string, updates: Partial<Announcement>) => {
    const response = await api.patch<{ announcement: Announcement }>(`/teams/${teamId}/announcements/${announcementId}`, updates);
    return response.data.announcement;
  },
  deleteAnnouncement: async (teamId: string, announcementId: string) => {
    const response = await api.delete(`/teams/${teamId}/announcements/${announcementId}`);
    return response.data;
  },
};

export interface ParentLink {
  id: string;
  parentId: string;
  childId: string;
  relationship: 'parent' | 'guardian' | 'grandparent' | 'other';
  canViewSchedule: boolean;
  canViewRoster: boolean;
  canViewStats: boolean;
  canReceiveNotifications: boolean;
  status: 'pending' | 'active' | 'inactive';
  invitedBy?: string;
  approvedAt?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  parent?: any;
  child?: any;
  inviter?: any;
}

export interface ParentDashboard {
  children: ParentLink[];
  teams: any[];
  upcomingEvents: Event[];
  notifications: Notification[];
}

export const parentPortalAPI = {
  getChildren: async () => {
    const response = await api.get<ParentLink[]>('/parent-portal/children');
    return response.data;
  },
  getParents: async () => {
    const response = await api.get<ParentLink[]>('/parent-portal/parents');
    return response.data;
  },
  inviteParent: async (childId: string, parentEmail: string, relationship?: string, permissions?: any) => {
    const response = await api.post<ParentLink>('/parent-portal/invite', { childId, parentEmail, relationship, permissions });
    return response.data;
  },
  approveLink: async (linkId: string) => {
    const response = await api.patch<ParentLink>(`/parent-portal/${linkId}/approve`);
    return response.data;
  },
  updatePermissions: async (linkId: string, permissions: Partial<ParentLink>) => {
    const response = await api.patch<ParentLink>(`/parent-portal/${linkId}/permissions`, permissions);
    return response.data;
  },
  removeLink: async (linkId: string) => {
    const response = await api.delete(`/parent-portal/${linkId}`);
    return response.data;
  },
  getChildTeams: async (childId: string) => {
    const response = await api.get(`/parent-portal/children/${childId}/teams`);
    return response.data;
  },
  getChildEvents: async (childId: string) => {
    const response = await api.get<Event[]>(`/parent-portal/children/${childId}/events`);
    return response.data;
  },
  getChildAttendanceSummary: async (childId: string) => {
    const response = await api.get(`/parent-portal/children/${childId}/attendance-summary`);
    return response.data;
  },
  getDashboard: async () => {
    const response = await api.get<ParentDashboard>('/parent-portal/dashboard');
    return response.data;
  },
};

export const usersAPI = {
  search: async (params: { q?: string; role?: string; skillLevel?: string; ageGroup?: string }) => {
    const response = await api.get<{ users: any[] }>('/users/search', { params });
    return response.data.users;
  },
};

export const uploadAPI = {
  uploadAvatar: async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('avatar', file);
    const response = await api.post<{ avatarUrl: string }>('/upload/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.avatarUrl;
  },
};

export const adminAPI = {
  getStats: async () => {
    const response = await api.get<{
      users: number;
      teams: number;
      posts: number;
      messages: number;
      pendingReports: number;
      newUsersLast30Days: number;
    }>('/admin/stats');
    return response.data;
  },
  getUsers: async (params?: { q?: string; role?: string; limit?: number; offset?: number }) => {
    const response = await api.get<{ total: number; users: any[] }>('/admin/users', { params });
    return response.data;
  },
  updateUserRole: async (userId: string, role: string) => {
    const response = await api.patch(`/admin/users/${userId}/role`, { role });
    return response.data;
  },
  deleteUser: async (userId: string) => {
    const response = await api.delete(`/admin/users/${userId}`);
    return response.data;
  },
  getReports: async (params?: { status?: string; limit?: number; offset?: number }) => {
    const response = await api.get<{ total: number; reports: any[] }>('/admin/reports', { params });
    return response.data;
  },
  updateReport: async (reportId: string, status: 'reviewed' | 'dismissed') => {
    const response = await api.patch(`/admin/reports/${reportId}`, { status });
    return response.data;
  },
  sendDigest: async () => {
    const response = await api.post<{ message: string; sent: number }>('/admin/send-digest');
    return response.data;
  },
  getMetrics: async () => {
    const response = await api.get<{
      signupsByWeek: Array<{ week: string; player: number; team_manager: number; parent: number; admin: number; total: number }>;
      inventory: { activeTeams: number; activePosts: number; upcomingTryouts: number; distinctCities: number };
      topCities: Array<{ location: string; teams: number }>;
      rsvpHealth: { tryoutsWithRsvps: number; tryoutsWithoutRsvps: number };
      featureAdoption: { totalSavedFilters: number; savedFiltersWithAlerts: number; totalHighlights: number; usersWithHighlights: number };
    }>('/admin/metrics');
    return response.data;
  },
};

export const applicationsAPI = {
  apply: async (postId: string, coverMessage?: string) => {
    const response = await api.post(`/posts/${postId}/apply`, { coverMessage });
    return response.data;
  },
  getForPost: async (postId: string) => {
    const response = await api.get<{ applications: any[] }>(`/posts/${postId}/applications`);
    return response.data.applications;
  },
  updateStatus: async (id: string, status: 'accepted' | 'rejected') => {
    const response = await api.patch(`/applications/${id}/status`, { status });
    return response.data;
  },
  withdraw: async (id: string) => {
    const response = await api.delete(`/applications/${id}`);
    return response.data;
  },
  getMy: async () => {
    const response = await api.get<{ applications: any[] }>('/applications/my');
    return response.data.applications;
  },
};

export const reviewsAPI = {
  create: async (data: { subjectId: string; subjectType: 'user' | 'team'; rating: number; comment?: string; season?: string }) => {
    const response = await api.post('/reviews', data);
    return response.data;
  },
  getForUser: async (userId: string) => {
    const response = await api.get<{ reviews: any[]; averageRating: number; count: number }>(`/reviews/user/${userId}`);
    return response.data;
  },
  getForTeam: async (teamId: string) => {
    const response = await api.get<{ reviews: any[]; averageRating: number; count: number }>(`/reviews/team/${teamId}`);
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/reviews/${id}`);
    return response.data;
  },
};

export const endorsementsAPI = {
  create: async (playerId: string, skill: string) => {
    const response = await api.post('/endorsements', { playerId, skill });
    return response.data;
  },
  delete: async (id: string) => {
    const response = await api.delete(`/endorsements/${id}`);
    return response.data;
  },
  getForUser: async (userId: string) => {
    const response = await api.get<{ endorsements: Array<{ skill: string; count: number; endorsers: string[]; endorsementId?: string }> }>(`/endorsements/user/${userId}`);
    return response.data.endorsements;
  },
};

export const tryoutsAPI = {
  getAll: async (filters?: { sport?: string; state?: string; city?: string; page?: number; limit?: number }) => {
    const response = await api.get<{ data: any[]; total: number; page: number; totalPages: number }>('/tryouts', { params: filters });
    return response.data;
  },
  getById: async (eventId: string) => {
    const response = await api.get(`/tryouts/${eventId}`);
    return response.data;
  },
  rsvp: async (eventId: string) => {
    const response = await api.post(`/tryouts/${eventId}/rsvp`);
    return response.data;
  },
  cancelRsvp: async (eventId: string) => {
    const response = await api.delete(`/tryouts/${eventId}/rsvp`);
    return response.data;
  },
};

export interface Highlight {
  id: string;
  userId: string;
  type: 'upload' | 'link';
  provider: 'youtube' | 'vimeo' | 'hudl' | 'upload';
  url: string;
  originalUrl?: string | null;
  title?: string | null;
  thumbnailUrl?: string | null;
  sortOrder: number;
  createdAt: string;
}

export const highlightsAPI = {
  getForUser: async (userId: string) => {
    const response = await api.get<{ highlights: Highlight[] }>(`/highlights/user/${userId}`);
    return response.data.highlights;
  },
  addLink: async (url: string, title?: string) => {
    const response = await api.post<{ highlight: Highlight }>('/highlights', { url, title });
    return response.data.highlight;
  },
  upload: async (file: File, title?: string) => {
    const formData = new FormData();
    formData.append('video', file);
    if (title) formData.append('title', title);
    const response = await api.post<{ highlight: Highlight }>('/highlights/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.highlight;
  },
  remove: async (id: string) => {
    const response = await api.delete(`/highlights/${id}`);
    return response.data;
  },
};

export interface TryoutCriterion {
  id: string;
  eventId: string;
  name: string;
  maxScore: number;
  sortOrder: number;
}

export interface TryoutParticipant {
  id: string;
  eventId: string;
  userId?: string | null;
  name: string;
  status: 'pending' | 'invited' | 'declined';
  notes?: string | null;
  user?: { id: string; firstName: string; lastName: string; avatarUrl?: string | null } | null;
  scores: Array<{ id: string; criterionId: string; score: number }>;
}

export const tryoutScoringAPI = {
  getSheet: async (eventId: string) => {
    const response = await api.get<{ event: any; criteria: TryoutCriterion[]; participants: TryoutParticipant[] }>(`/tryouts/${eventId}/scoring`);
    return response.data;
  },
  addCriterion: async (eventId: string, name: string, maxScore?: number) => {
    const response = await api.post<{ criterion: TryoutCriterion }>(`/tryouts/${eventId}/criteria`, { name, maxScore });
    return response.data.criterion;
  },
  deleteCriterion: async (eventId: string, criterionId: string) => {
    const response = await api.delete(`/tryouts/${eventId}/criteria/${criterionId}`);
    return response.data;
  },
  addParticipant: async (eventId: string, data: { name?: string; userId?: string }) => {
    const response = await api.post<{ participant: TryoutParticipant }>(`/tryouts/${eventId}/participants`, data);
    return response.data.participant;
  },
  importRsvps: async (eventId: string) => {
    const response = await api.post<{ imported: number; totalRsvps: number }>(`/tryouts/${eventId}/participants/import-rsvps`);
    return response.data;
  },
  updateParticipant: async (eventId: string, participantId: string, updates: { status?: string; notes?: string; name?: string }) => {
    const response = await api.patch<{ participant: TryoutParticipant }>(`/tryouts/${eventId}/participants/${participantId}`, updates);
    return response.data.participant;
  },
  deleteParticipant: async (eventId: string, participantId: string) => {
    const response = await api.delete(`/tryouts/${eventId}/participants/${participantId}`);
    return response.data;
  },
  setScore: async (eventId: string, participantId: string, criterionId: string, score: number) => {
    const response = await api.put(`/tryouts/${eventId}/scores`, { participantId, criterionId, score });
    return response.data;
  },
};

export const usersDigestAPI = {
  updateDigest: async (emailDigest: boolean) => {
    const response = await api.patch('/users/me/digest', { emailDigest });
    return response.data;
  },
};

export const subscriptionsAPI = {
  getPlans: async () => {
    const response = await api.get('/subscriptions/plans');
    return response.data;
  },
  getStatus: async () => {
    const response = await api.get('/subscriptions/status');
    return response.data;
  },
  createCheckoutSession: async (planId: string) => {
    const response = await api.post('/subscriptions/create-checkout-session', { planId });
    return response.data;
  },
  cancel: async () => {
    const response = await api.post('/subscriptions/cancel');
    return response.data;
  },
};

export default api;
